// src/pages/BusinessPlanner.jsx — Stratos: Business Strategy Terminal (Grok)
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendTerminalStreaming } from '../services/terminalChat';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import FileTreeSidebar from '../components/FileTreeSidebar';
import { loadSessions as loadServerSessions, saveSession as saveServerSession, deleteSession as deleteServerSession } from '../services/sessionSync';

const STORAGE_KEY = 'stratosSessions';
const CURRENT_KEY = 'stratosCurrentSessionId';

const createNewSession = (idSuffix = '') => {
  const ts = Date.now();
  return {
    id: `stratos-${ts}${idSuffix}`,
    title: `Strategy ${new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' })}`,
    messages: [],
    createdAt: ts,
  };
};

function BusinessPlanner() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const [streamingContent, setStreamingContent] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isSessionDropdownOpen, setIsSessionDropdownOpen] = useState(false);
  const [grokUnavailable, setGrokUnavailable] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionDropdownRef = useRef(null);
  const sessionButtonRef = useRef(null);

  const MIN_INTERVAL = 3000;

  // ── localStorage init ──
  useEffect(() => {
    let init = [];
    let initId = null;
    (async () => {
      const serverSessions = await loadServerSessions('stratos');
      if (serverSessions && serverSessions.length > 0) {
        init = serverSessions;
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(init)); } catch {}
      } else {
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) init = JSON.parse(stored);
        } catch {}
      }
      if (init.length > 0) {
        const storedId = localStorage.getItem(CURRENT_KEY);
        initId = (storedId && init.find(s => s.id === storedId)) ? storedId : init.sort((a, b) => b.createdAt - a.createdAt)[0].id;
      } else {
        const s = createNewSession('-init');
        init = [s]; initId = s.id;
      }
      setSessions(init);
      setCurrentSessionId(initId);
    })();
  }, []);

  useEffect(() => {
    if (sessions.length > 0 && currentSessionId) {
      const t = setTimeout(() => {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
          localStorage.setItem(CURRENT_KEY, currentSessionId);
        } catch {}
        const current = sessions.find(s => s.id === currentSessionId);
        if (current && current.messages.length > 0) {
          saveServerSession('stratos', current);
        }
      }, 1000);
      return () => clearTimeout(t);
    }
  }, [sessions, currentSessionId]);

  const currentSession = useMemo(() => sessions.find(s => s.id === currentSessionId), [sessions, currentSessionId]);
  const currentMessages = useMemo(() => currentSession?.messages || [], [currentSession]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [currentMessages, streamingContent]);

  useEffect(() => {
    const handler = (e) => {
      if (isSessionDropdownOpen && sessionDropdownRef.current && !sessionDropdownRef.current.contains(e.target) && sessionButtonRef.current && !sessionButtonRef.current.contains(e.target))
        setIsSessionDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isSessionDropdownOpen]);

  const handleNewChat = () => {
    const s = createNewSession();
    setSessions(prev => [s, ...prev].sort((a, b) => b.createdAt - a.createdAt));
    setCurrentSessionId(s.id);
    setInput('');
    setIsLoading(false);
    setIsSessionDropdownOpen(false);
  };

  const handleSelectSession = (id) => { setCurrentSessionId(id); setInput(''); setIsSessionDropdownOpen(false); };

  const handleDeleteSession = (id, e) => {
    e.stopPropagation();
    deleteServerSession(id);
    setSessions(prev => {
      const remaining = prev.filter(s => s.id !== id);
      if (remaining.length === 0) { const s = createNewSession('-empty'); setCurrentSessionId(s.id); return [s]; }
      if (currentSessionId === id) setCurrentSessionId([...remaining].sort((a, b) => b.createdAt - a.createdAt)[0].id);
      return remaining;
    });
  };

  const handleSend = async () => {
    if (!input.trim() || !currentSessionId) return;
    const now = Date.now();
    if (now - lastRequestTime < MIN_INTERVAL) { alert(`Please wait ${Math.ceil((MIN_INTERVAL - (now - lastRequestTime)) / 1000)}s`); return; }
    setLastRequestTime(now);

    const userMsg = { role: 'user', content: input };
    const session = sessions.find(s => s.id === currentSessionId);
    if (!session) return;
    const msgs = [...session.messages, userMsg];
    let title = session.title;
    if (session.messages.length === 0 && session.title.startsWith('Strategy'))
      title = `Strategy: ${input.substring(0, 25)}${input.length > 25 ? '...' : ''}`;

    setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: msgs, title } : s));
    const currentInput = input;
    setInput('');
    setIsLoading(true);
    setGrokUnavailable(false);

    try {
      const recentHistory = msgs.slice(-6);
      let acc = '';
      await sendTerminalStreaming('/grok/stream', currentInput, recentHistory, {
        onStatus: (s) => setStatusMessage(s),
        onChunk: (text) => { acc += text; setStreamingContent(acc); setStatusMessage(''); },
        onDone: (result) => {
          setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: [...msgs, { role: 'assistant', content: result.content }] } : s));
          setStreamingContent('');
          setStatusMessage('');
        },
        onError: (err) => {
          console.error('Stratos stream error:', err);
          setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: [...msgs, { role: 'assistant', content: `Error: ${err?.error || 'Unknown error'}` }] } : s));
          setStreamingContent('');
          setStatusMessage('');
        },
      });
    } catch (error) {
      console.error('Stratos streaming failed:', error);
      const errorText = error.message || '';
      if (errorText.includes('503') || errorText.includes('XAI_API_KEY')) {
        setGrokUnavailable(true);
      }
      setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: [...msgs, { role: 'assistant', content: errorText.includes('XAI_API_KEY') ? 'Grok API key is not configured yet. See setup instructions in the app.' : `Connection error: ${error.message}` }] } : s));
      setStreamingContent('');
      setStatusMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const mdComponents = {
    h1: ({ node, ...p }) => <h1 className="text-lg font-bold text-white mt-2 mb-2" {...p} />,
    h2: ({ node, ...p }) => <h2 className="text-md font-semibold text-white mt-2 mb-1" {...p} />,
    h3: ({ node, ...p }) => <h3 className="text-sm font-semibold text-zinc-300 mt-1 mb-1" {...p} />,
    ul: ({ node, ...p }) => <ul className="list-disc pl-5 mb-2 space-y-1" {...p} />,
    ol: ({ node, ...p }) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...p} />,
    p: ({ node, ...p }) => <p className="mb-2 last:mb-0" {...p} />,
    a: ({ node, ...p }) => <a className="text-violet-400 underline" {...p} />,
    strong: ({ node, ...p }) => <strong className="font-bold text-white" {...p} />,
    code: ({ node, inline, ...p }) =>
      inline
        ? <code className="px-1.5 py-0.5 bg-zinc-800 text-violet-300 rounded text-xs font-mono" {...p} />
        : <code className="block bg-zinc-900 border border-zinc-700 p-3 rounded-lg text-xs overflow-x-auto my-2 font-mono" {...p} />,
    table: ({ node, ...p }) => <div className="overflow-x-auto my-4"><table className="min-w-full divide-y divide-zinc-700 border border-zinc-700" {...p} /></div>,
    thead: ({ node, ...p }) => <thead className="bg-zinc-900" {...p} />,
    tbody: ({ node, ...p }) => <tbody className="divide-y divide-zinc-800" {...p} />,
    th: ({ node, ...p }) => <th className="px-3 py-2 text-left text-xs font-semibold text-zinc-200 border-r border-zinc-700 last:border-r-0" {...p} />,
    td: ({ node, ...p }) => <td className="px-3 py-2 text-xs text-zinc-400 border-r border-zinc-800 last:border-r-0" {...p} />,
  };

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 50, background: '#09090b', color: '#e4e4e7', fontFamily: 'sans-serif' }}>
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/health')} title="Back to AI Sentry"
            className="w-8 h-8 bg-zinc-800 hover:bg-violet-500 rounded-xl flex items-center justify-center transition-colors">
            <svg className="w-4 h-4 text-zinc-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" /></svg>
          </button>
          <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">S</span>
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">STRATOS</h1>
            <p className="text-[10px] text-zinc-500">Business Strategy &bull; Powered by Grok</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Session selector */}
          <div className="relative">
            <button ref={sessionButtonRef} onClick={() => setIsSessionDropdownOpen(!isSessionDropdownOpen)}
              className="flex items-center px-3 py-1.5 text-xs bg-zinc-950 border border-zinc-700 rounded-lg hover:border-violet-500 transition-colors">
              {currentSession?.title || 'Select'}
              <svg className="h-3 w-3 ml-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </button>
            {isSessionDropdownOpen && (
              <div ref={sessionDropdownRef} className="absolute right-0 mt-2 w-72 bg-zinc-900 border border-zinc-700 rounded-lg shadow-lg z-20 max-h-80 overflow-y-auto">
                <div className="p-2">
                  <button onClick={handleNewChat} className="w-full flex items-center px-3 py-2 text-xs font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-500 transition-colors mb-2">
                    + New Session
                  </button>
                  <div className="border-t border-zinc-700 pt-2 space-y-0.5">
                    {sessions.map(s => (
                      <div key={s.id} onClick={() => handleSelectSession(s.id)}
                        className={`flex items-center px-3 py-2 text-xs rounded-lg cursor-pointer group ${currentSessionId === s.id ? 'bg-violet-500/10 text-violet-300' : 'text-zinc-400 hover:bg-zinc-800'}`}>
                        <div className="flex-1 truncate">
                          <div className="font-medium truncate">{s.title}</div>
                          <div className="text-[10px] text-zinc-600">{s.messages.length} msgs</div>
                        </div>
                        <button onClick={(e) => handleDeleteSession(s.id, e)} className="ml-2 p-1 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          {grokUnavailable && (
            <div className="text-[10px] bg-amber-500/20 text-amber-400 px-3 py-1 rounded border border-amber-500/30">
              GROK KEY REQUIRED
            </div>
          )}
        </div>
      </div>

      {/* Main content with sidebar */}
      <div className="flex flex-1 overflow-hidden">
        <FileTreeSidebar />

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-zinc-950">
            {currentMessages.length === 0 && !streamingContent && !isLoading ? (
              <div className="text-center py-16">
                <div className="mx-auto w-16 h-16 bg-violet-500/10 rounded-2xl flex items-center justify-center mb-6">
                  <span className="text-4xl">&#128202;</span>
                </div>
                <h2 className="text-2xl font-semibold mb-3">Business Strategy Terminal</h2>
                <p className="max-w-md mx-auto text-zinc-400 text-sm mb-8">
                  Ask about market analysis, product strategy, competitive moves, fundraising, operations, or any business decision.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  {[
                    '90-Day GTM Plan',
                    'Competitive Analysis',
                    'Fundraising Strategy',
                  ].map(label => (
                    <button key={label} onClick={() => setInput(label === '90-Day GTM Plan' ? 'Create a 90-day go-to-market plan for our new AI product' : label === 'Competitive Analysis' ? 'Analyze competitive threats in the hyperscale data center market' : 'Design a Series A fundraising strategy for an AI infrastructure startup')}
                      className="px-4 py-2 text-xs bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors">
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {currentMessages.map((msg, idx) => (
                  <div key={`${currentSessionId}-${idx}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'user' ? (
                      <div className="flex items-end max-w-[75%]">
                        <div className="bg-zinc-800 border border-zinc-700 px-4 py-3 rounded-2xl rounded-br-md">
                          <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start max-w-[85%]">
                        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mr-2 text-xs font-bold text-white">S</div>
                        <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 rounded-2xl rounded-bl-md">
                          <div className="text-sm prose prose-sm max-w-none text-zinc-300">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{msg.content}</ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(isLoading || streamingContent) && (
              <div className="flex items-start justify-start mt-4">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mr-2 text-xs font-bold text-white">S</div>
                <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 rounded-2xl rounded-bl-md max-w-[85%]">
                  {streamingContent ? (
                    <div className="text-sm prose prose-sm max-w-none text-zinc-300">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{streamingContent}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3">
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-xs text-zinc-500">{statusMessage || 'Stratos is thinking...'}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-zinc-900 border-t border-zinc-800">
            <div className="flex gap-3">
              <textarea
                rows="2"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="What business decision should we analyze today?"
                className="flex-1 bg-zinc-950 border border-zinc-700 rounded-xl px-4 py-3 focus:border-violet-500 outline-none resize-none text-sm"
                disabled={isLoading}
              />
              <button onClick={handleSend} disabled={isLoading || !input.trim()}
                className="bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-700 px-6 rounded-xl font-medium transition-colors disabled:cursor-not-allowed">
                Send
              </button>
            </div>
            <div className="text-[10px] text-zinc-600 mt-2 text-center">
              Private &bull; Grok &bull; Enterprise
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BusinessPlanner;
